import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import DataTable from '../components/DataTable';

const ListarAprendices = () => {
  const [aprendices, setAprendices] = useState([]);
  const navigate = useNavigate();

  // Función para descargar archivos docx
  const descargarDocx = async (url, nombreArchivo) => {
    try {
      const response = await axios.get(url, { responseType: 'blob' });
      const urlBlob = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = urlBlob;
      link.setAttribute('download', nombreArchivo);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(urlBlob);
    } catch (error) {
      console.error('Error al descargar el documento:', error);
      Swal.fire('Error', 'No se pudo generar el documento.', 'error');
    }
  };

  const getData = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/aprendices/listall/');

      if (!data || !Array.isArray(data.aprendices)) {
        throw new Error('Datos de aprendices no válidos.');
      }

      const aprendicesList = data.aprendices;

      // 1. Extraer todos los IDs
      const ids = aprendicesList.map(aprendiz => aprendiz._id);

      // 2. Consultar tests existentes de todos los aprendices
      let kolbMap = {};
      try {
        const resTests = await axios.post('/api/testkolb/existencias', { ids });
        if (resTests.data.ok && resTests.data.kolbMap) {
          kolbMap = resTests.data.kolbMap;
        }
      } catch (err) {
        console.error('Error al obtener los testKolb masivamente:', err.message);
      }

      // 3. Armar estructura final
      const aprendicesConFunciones = aprendicesList.map((aprendiz) => {
        const testId = kolbMap[aprendiz._id] || null;

        return {
          ...aprendiz,
          imagen: (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: 50,
                height: 50,
              }}
            >
              {aprendiz.imgAprendiz ? (
                <img
                  src={aprendiz.imgAprendiz}
                  alt="Aprendiz"
                  className="rounded-circle border border-2 border-dark"
                  style={{ width: 50, height: 50, objectFit: 'cover' }}
                />
              ) : (
                <i
                  className="fa-solid fa-user rounded-circle border border-2 border-dark"
                  style={{
                    fontSize: 30,
                    width: 50,
                    height: 50,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                />
              )}
            </div>
          ),
          funciones: (
            <div className="row g-2 align-items-center">
              {/* Botones */}
              <div className="col-12 col-md-auto">
                <div className="d-flex justify-content-center justify-content-md-start flex-wrap gap-2">
                  <button
                    className="btn btn-warning d-flex align-items-center justify-content-center"
                    style={{ width: 30, height: 30, padding: 0 }}
                    onClick={() =>
                      navigate(`/aprendiz/registraraprendices/${aprendiz._id}`, { state: { aprendiz } })
                    }
                    title="Editar Aprendiz"
                  >
                    <i className="fa-solid fa-edit" />
                  </button>

                  {testId ? (
                    <button
                      className="btn btn-warning d-flex align-items-center justify-content-center"
                      style={{ width: 30, height: 30, padding: 0 }}
                      onClick={() =>
                        navigate(`/aprendiz/testkolb/editar/${testId}`, { state: { aprendiz } })
                      }
                      title="Editar Test de Kolb"
                    >
                      <i className="fas fa-brain" />
                    </button>
                  ) : (
                    <button
                      className="btn"
                      style={{
                        width: 30,
                        height: 30,
                        padding: 0,
                        backgroundColor: '#6f42c1',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      onClick={() =>
                        navigate(`/aprendiz/testkolb/registrar/${aprendiz._id}`, { state: { aprendiz } })
                      }
                      title="Test de Kolb"
                    >
                      <i className="fas fa-brain" />
                    </button>
                  )}

                  <button
                    className="btn btn-primary d-flex align-items-center justify-content-center"
                    style={{ width: 30, height: 30, padding: 0 }}
                    onClick={() =>
                      descargarDocx(
                        `/api/compromiso/generar-docx/${aprendiz._id}`,
                        `compromiso_${aprendiz.documentoAprendiz}.docx`
                      )
                    }
                    title="Compromiso del Aprendiz"
                  >
                    <i className="fa-solid fa-file-word fa-lg" style={{ color: '#fff' }} />
                  </button>

                  <button
                    className="btn btn-secondary d-flex align-items-center justify-content-center"
                    style={{ width: 30, height: 30, padding: 0 }}
                    onClick={() =>
                      descargarDocx(
                        `/api/tratamiento/generar-docx/${aprendiz._id}`,
                        `tratamiento_${aprendiz.documentoAprendiz}.docx`
                      )
                    }
                    title="Tratamiento de Datos"
                  >
                    <i className="fa-solid fa-file-word fa-lg" style={{ color: '#fff' }} />
                  </button>

                  <button
                    className="btn btn-danger d-flex align-items-center justify-content-center"
                    style={{ width: 30, height: 30, padding: 0 }}
                    onClick={() => deleteAprendiz(aprendiz._id)}
                    title="Eliminar Aprendiz"
                  >
                    <i className="fa-solid fa-trash fa-lg" />
                  </button>
                </div>
              </div>

              {/* Select de Hoja de Vida */}
              <div className="col-12 col-md-auto">
                <select
                  className="form-control form-select-sm w-100"
                  defaultValue=""
                  onChange={(e) => {
                    const modelo = e.target.value;
                    if (modelo) {
                      descargarDocx(
                        `/api/hojavida/generar-docx/${aprendiz._id}?modelohv=${modelo}`,
                        `hoja_vida_${modelo}_${aprendiz.documentoAprendiz}.docx`
                      );
                      e.target.value = '';
                    }
                  }}
                  title="Selecciona Hoja de Vida"
                >
                  <option value="" disabled>
                    Selecciona Hoja de Vida
                  </option>
                  <option value="modelo1">Modelo 1</option>
                  <option value="modelo2">Modelo 2</option>
                  <option value="modelo3">Modelo 3</option>
                  <option value="modelo4">Modelo 4</option>
                  <option value="modelo5">Modelo 5</option>
                </select>
              </div>
            </div>

          ),
        };
      });

      setAprendices(aprendicesConFunciones);
    } catch (error) {
      console.error('Error al cargar los aprendices:', error.message);
      Swal.fire('Error', 'No se pudieron cargar los aprendices.', 'error');
    }
    // eslint-disable-next-line 
  }, []);

  useEffect(() => {
    getData();
  }, [getData]);


  const deleteAprendiz = async (id) => {
    try {
      const result = await Swal.fire({
        title: '¿Está seguro?',
        text: '¡No podrá revertir este proceso!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Sí, eliminar!',
      });

      if (result.isConfirmed) {
        const { data } = await axios.delete(`/api/aprendices/${id}`);
        await getData();

        await Swal.fire({
          icon: 'success',
          title: data.message,
          showConfirmButton: false,
          timer: 1500,
        });

      }
    } catch (error) {
      console.error('Error al eliminar el aprendiz:', error.message);
      Swal.fire('Error', 'No se pudo eliminar el aprendiz.', 'error');
    }
  }




  const columns = React.useMemo(
    () => [
      { accessor: 'imagen' },
      { Header: 'Documento', accessor: 'documentoAprendiz' },
      { Header: 'Nombre', accessor: 'nombre1Aprendiz' },
      { Header: 'Apellido', accessor: 'apellido1Aprendiz' },
      { Header: 'Edad', accessor: 'edadAprendiz' },
      { Header: 'Sexo', accessor: 'sexoAprendiz' },
      { Header: 'Email', accessor: 'emailAprendiz' },
      { accessor: 'funciones' },
    ],
    []
  );

  return (
    <div className="container-fluid py-1 px-0 shadow-lg rounded-4 bg-light mt-5">
      <div className="py-3">
        <h1>Lista de Aprendices</h1>
        {Array.isArray(aprendices) && aprendices.length > 0 ? (
          <DataTable columns={columns} data={aprendices} />
        ) : (
          <p>Cargando aprendices...</p>
        )}
      </div>
    </div>

  );
};

export default ListarAprendices;
